

import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  Typography,
  Box,
  TextField,
  MenuItem,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import Spinner from "../../../components/common/Spinner";
import { usePermission } from "../../../permissions/usePermission";
import ApiCallService from "../../../Service/ApiCallService";
import {
  canOpenNetworkMemberProfile,
  fetchIsNecCouncilMember,
  NEC_COUNCIL_NAME,
} from "../../../utils/necMembership";
import dummyImage from "../../../images/user.png";
import { getPublicS3Url } from "../../../utils/s3PublicUrl";

const extractStringId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.$oid === "string") return value.$oid;
    if (value._id) return extractStringId(value._id);
  }
  return "";
};

const getMemberProfileUserId = (member) =>
  extractStringId(member?.userId) ||
  extractStringId(member?.user?._id) ||
  extractStringId(member?.user) ||
  extractStringId(member?._id);

export default function MembersList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [members, setMembers] = useState([]);
  const [councilsData, setCouncilsData] = useState([]);
  const [committeeOptions, setCommitteeOptions] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const [selectedCouncil, setSelectedCouncil] = useState("All");
  const [selectedCommittee, setSelectedCommittee] = useState("All");

  const [loading, setLoading] = useState(false);
  const [isNecCouncilMember, setIsNecCouncilMember] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let active = true;

    const fetchMembership = async () => {
      const isNecMember = await fetchIsNecCouncilMember();
      if (active) {
        setIsNecCouncilMember(isNecMember);
      }
    };

    fetchMembership();

    return () => {
      active = false;
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { canByKey, hasPolicyRole, isSuperAdmin } = usePermission();
  const userDetails = useSelector((state) => state.user?.userDetails);
  const canViewMember =
    canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_VIEW") ||
    canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_DETAILS");
  const canOpenMemberCard =
    canViewMember ||
    canOpenNetworkMemberProfile({
      userDetails,
      isSuperAdmin,
      hasPolicyRole,
      canByKey,
    }) || isNecCouncilMember;
  const navigateFrom = location.pathname.includes("committeeMembersList")
    ? "committeeMembersList"
    : "network";
  const permissionsState = useSelector((state) => state.permissions);

  // ---------------- COMMITTEE ORDER ----------------
  const COMMITTEE_ORDER = [
    "governing committee",
    "operations committee",
    "national program head",
    "state presidents & state general secretaries",
  ];

  // ---------------- NORMALIZED STYLE HELPER ----------------
  const getCommitteeStyle = (group) => {
    const normalized = group?.toLowerCase().trim();

    const map = Object.entries(theme.committeeStyles || {}).reduce(
      (acc, [key, value]) => {
        acc[key.toLowerCase()] = value;
        return acc;
      },
      {}
    );

    return map[normalized] || theme.committeeStyles?.default;
  };

  // ---------------- PERMISSION LOGIC ----------------
  const scopes = permissionsState?.permissions?.scopes || [];

  const committeeScopes = scopes.filter(
    (scope) => scope?.sourceType === "COMMITTEE"
  );

  const scopeLevelRank = {
    NATIONAL: 0,
    STATE: 1,
    REGION: 2,
    DISTRICT: 3,
  };

  const councilLevelRank = {
    National: 0,
    State: 1,
    Regional: 2,
    District: 3,
  };

  const scopeRank = committeeScopes.reduce((acc, scope) => {
    const rank = scopeLevelRank[scope?.scope?.level] ?? 99;
    return Math.min(acc, rank);
  }, 99);

  const visibleCouncilsData = useMemo(() => {
    if (scopeRank === 99) return councilsData;

    return councilsData.filter((c) => {
      const rank = councilLevelRank[c?.level] ?? 99;
      return rank >= scopeRank;
    });
  }, [councilsData, scopeRank]);

  const visibleCouncilOptions = useMemo(() => {
    const names = visibleCouncilsData.map((c) => c?.councilName);
    return ["All", ...new Set(names)];
  }, [visibleCouncilsData]);

  // ---------------- FETCH ----------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [membersRes, councilsRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BASE_URL}/committeeMembers/allMembers`
          ),
          axios.get(
            `${process.env.REACT_APP_BASE_URL}/committeeDetails/councils`
          ),
        ]);

        if (membersRes.data?.success) {
          const formatted = membersRes.data.data.map((m) => ({
            _id: m.user._id,
            userFirstName: m.user.userFirstName,
            userLastName: m.user.userLastName,
            userImageURL: m.user.userImageURL,
            committeeType: m.committee.committeeType?.name || "",
            position:
              m.designations?.map((d) => d.title).join(", ") || "",
            council:
              m.committee.committeeType?.councilId?.councilName || "",
          }));

          setMembers(formatted);
        }

        setCouncilsData(councilsRes.data?.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ---------------- FETCH COMMITTEE ----------------
  useEffect(() => {
    const fetchCommittees = async () => {
      if (selectedCouncil === "All") {
        setCommitteeOptions(["All"]);
        return;
      }

      const council = councilsData.find(
        (c) => c?.councilName === selectedCouncil
      );

      if (!council?._id) return;

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/committeeDetails/committeesByCouncil`,
          { params: { councilId: council._id } }
        );

        const names = [
          "All",
          ...new Set(res.data?.data?.map((c) => c.name)),
        ];

        setCommitteeOptions(names);
      } catch {
        setCommitteeOptions(["All"]);
      }
    };

    fetchCommittees();
  }, [selectedCouncil, councilsData]);

  // ---------------- FILTER ----------------
  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      const name = `${m.userFirstName} ${m.userLastName}`.toLowerCase();

      return (
        name.includes(searchTerm.toLowerCase()) &&
        (selectedCouncil === "All" || m.council === selectedCouncil) &&
        (selectedCommittee === "All" ||
          m.committeeType === selectedCommittee)
      );
    });
  }, [members, searchTerm, selectedCouncil, selectedCommittee]);

  // ---------------- GROUPING ----------------
  const groupedMembers = useMemo(() => {
    const grouped = {};

    filteredMembers.forEach((m) => {
      const key = m.committeeType || "Others";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(m);
    });

    return grouped;
  }, [filteredMembers]);

  // ---------------- SORT ----------------
  const sortedGroups = useMemo(() => {
    return Object.entries(groupedMembers).sort(([a], [b]) => {
      const indexA = COMMITTEE_ORDER.indexOf(a.toLowerCase());
      const indexB = COMMITTEE_ORDER.indexOf(b.toLowerCase());

      return (
        (indexA === -1 ? 999 : indexA) -
        (indexB === -1 ? 999 : indexB)
      );
    });
  }, [groupedMembers]);

  // ---------------- UI ----------------
  return (
    <div style={{ backgroundColor: "#fff" }}>
      <Typography variant="h4" sx={{ fontWeight: "bold", ml: 2, mt: 1 }}>
        Office-Bearer List
      </Typography>

      {/* FILTER BAR */}
      <Box sx={{ borderRadius: 4, p: isMobile ? 1 : 2 }}>
        <Box sx={{ position: "relative", display: "flex", minHeight: "40px" }}>
          <Box
            sx={{
              position: "absolute",
              left: 0,
              display: "flex",
              gap: 1,
              width: isMobile ? "100%" : "420px",
            }}
          >
            <TextField
              fullWidth
              select
              label="Council"
              value={selectedCouncil}
              onChange={(e) => setSelectedCouncil(e.target.value)}
            >
              {visibleCouncilOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label="Committee"
              value={selectedCommittee}
              onChange={(e) => setSelectedCommittee(e.target.value)}
            >
              {committeeOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ position: "absolute", right: 0 }}>
            {showSearch ? (
              <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            ) : (
              <IconButton onClick={() => setShowSearch(true)}>
                <SearchIcon />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {/* CONTENT */}
      {loading ? (
        <Spinner loading />
      ) : sortedGroups.length === 0 ? (
        <Typography sx={{ textAlign: "center", mt: 10 }}>
          No members found
        </Typography>
      ) : (
        sortedGroups.map(([group, list]) => {
          const style = getCommitteeStyle(group);

          return (
            <Box
              key={group}
              sx={{
                borderRadius: 4,
                mb: 4,
                px: 2,
                py: 2,
                background: style?.gradient,
              }}
            >
              <Typography variant="h5" fontWeight={600} align="center">
                {group}
              </Typography>

              <Grid container spacing={3} justifyContent="center">
                {list.map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member._id}>
                    <Card
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 3,
                        borderRadius: 3,
                        border: "1px solid #e6e6e6",
                        cursor: canOpenMemberCard ? "pointer" : "default",
                        opacity: canOpenMemberCard ? 1 : 0.7,
                      }}
                      onClick={() => {
                        if (!canOpenMemberCard) return;
                        const profileUserId = getMemberProfileUserId(member);
                        if (!profileUserId) return;
                        navigate(`/profile/${profileUserId}`, {
                          state: { 
                            mode: "view", 
                            navigateFrom,
                            canOpenMemberProfile: canOpenMemberCard
                          },
                        });
                      }}
                    >
                      <Box
                        sx={{
                          width: 100,
                          height: 100,
                          borderRadius: 2,
                          overflow: "hidden",
                          background: style?.solid,
                          mr: 2,
                        }}
                      >
                        <Box
                          component="img"
                          src={getPublicS3Url(member.userImageURL) || dummyImage}
                          sx={{ width: "100%", height: "100%" }}
                        />
                      </Box>

                      <Box>
                        <Typography fontWeight={700}>
                          {member.userFirstName} {member.userLastName}
                        </Typography>
                        <Typography variant="body2">
                          {member.position}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>


                ))}
              </Grid>
            </Box>
          );
        })
      )}
    </div>
  );
}
